/**
 * Batch Creation Component - Unified Upload Flow
 * Single-page form with file selection and automatic count
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import uploadApiService from '@/services/uploadApi';
import { useTranslation } from 'react-i18next';

interface FileWithStatus {
  file: File;
  status: 'valid' | 'invalid';
  error?: string;
}

const BatchCreation: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Form state
  const [clientName, setClientName] = useState('');
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // File validation
  const ALLOWED_TYPES = ['application/pdf', 'text/html', 'message/rfc822'];
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
  
  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type) && !file.name.toLowerCase().match(/\.(pdf|html|htm|eml)$/)) {
      return t('upload.validation.invalid_type', 'Invalid file type. Only PDF, HTML, and EML files are allowed.');
    }
    if (file.size > MAX_FILE_SIZE) {
      return t('upload.validation.file_too_large', 'File is too large. Maximum size is 50 MB.');
    }
    return null;
  };
  
  // Handle file selection
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    
    const newFiles: FileWithStatus[] = selectedFiles.map((file) => {
      const validationError = validateFile(file);
      return {
        file,
        status: validationError ? 'invalid' : 'valid',
        error: validationError || undefined,
      };
    });
    
    setFiles((prev) => [...prev, ...newFiles]);
    setError(null);
    
    // Reset input to allow selecting the same file again
    event.target.value = '';
  }, []);
  
  // Handle drag and drop
  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    
    const newFiles: FileWithStatus[] = droppedFiles.map((file) => {
      const validationError = validateFile(file);
      return {
        file,
        status: validationError ? 'invalid' : 'valid',
        error: validationError || undefined,
      };
    });
    
    setFiles((prev) => [...prev, ...newFiles]);
    setError(null);
  }, []);
  
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };
  
  // Remove file from list
  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };
  
  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };
  
  // Get valid files only
  const validFiles = files.filter(f => f.status === 'valid');
  const hasInvalidFiles = files.some(f => f.status === 'invalid');
  
  // Validation
  const canSubmit = clientName.trim().length > 0 && validFiles.length > 0 && !loading;
  
  // Handle confirmation dialog
  const handleOpenConfirmDialog = () => {
    if (!canSubmit) {
      setError(t('upload.validation.invalid_form', 'Please fill all fields correctly'));
      return;
    }
    setShowConfirmDialog(true);
  };
  
  const handleCloseConfirmDialog = () => {
    setShowConfirmDialog(false);
  };
  
  // Handle upload
  const handleConfirmUpload = async () => {
    setShowConfirmDialog(false);
    setLoading(true);
    setError(null);
    setUploadProgress(50); // Show progress during upload
    
    try {
      // 1. Create batch with automatic file count
      console.log(`📤 Creating batch for ${validFiles.length} files...`);
      const batchResponse = await uploadApiService.createBatch({
        client_name: clientName.trim(),
        total_files: validFiles.length, // ✅ Automatic!
      });
      
      const batchId = batchResponse.batch_id;
      console.log(`✅ Batch created: ${batchId}`);
      
      setUploadProgress(75); // Update progress
      
      // 2. Upload files
      console.log(`📤 Uploading ${validFiles.length} files...`);
      const filesToUpload = validFiles.map(f => f.file);
      
      await uploadApiService.uploadFiles(batchId, filesToUpload);
      
      console.log(`✅ Files uploaded successfully`);
      
      setUploadProgress(100); // Complete
      
      // 3. Navigate to batch details
      navigate(`/upload/batch/${batchId}`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || t('upload.errors.upload_failed', 'Failed to upload files');
      setError(errorMessage);
      console.error('❌ Upload failed:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            {t('upload.create.title', 'Upload Files')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('upload.create.subtitle_unified', 'Select your files and upload them in one step')}
          </Typography>
        </Box>
        
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* Upload Progress */}
        {loading && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('upload.progress.uploading', 'Uploading files...')} {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
        
        <Grid container spacing={3}>
          {/* Client Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('upload.create.client_name', 'Client Name')}
              placeholder={t('upload.create.client_placeholder', 'Le client correspond aux mentions d\'Investissement Québec, l\'acronyme IQ, ses gestionnaires principaux Bicha Ngo et Hubert Bolduc, et autres membres de la direction.')}
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              required
              disabled={loading}
              helperText={t('upload.create.client_help', 'Enter the client name for this batch')}
              multiline
              minRows={2}
              inputProps={{
                maxLength: 500,
              }}
            />
          </Grid>
          
          {/* File Drop Zone */}
          <Grid item xs={12}>
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                border: '2px dashed',
                borderColor: files.length > 0 ? 'success.main' : 'primary.main',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: files.length > 0 ? 'success.light' : 'action.hover',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: files.length > 0 ? 'success.light' : 'action.selected',
                  borderColor: files.length > 0 ? 'success.dark' : 'primary.dark',
                },
              }}
            >
              <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t('upload.dropzone.title', 'Drag and drop files here')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('upload.dropzone.subtitle', 'or')}
              </Typography>
              <Button
                component="label"
                variant="outlined"
                disabled={loading}
                sx={{ mt: 1 }}
              >
                {t('upload.dropzone.button', 'Select Files')}
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.html,.htm,.eml"
                  onChange={handleFileSelect}
                  disabled={loading}
                />
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
                {t('upload.dropzone.formats', 'Supported: PDF, HTML, EML · Max 50 MB per file')}
              </Typography>
            </Box>
          </Grid>
          
          {/* File List */}
          {files.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('upload.files.title', 'Selected Files')} ({validFiles.length})
              </Typography>
              
              {hasInvalidFiles && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('upload.files.invalid_warning', 'Some files are invalid and will not be uploaded')}
                </Alert>
              )}
              
              <List sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {files.map((fileWithStatus, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveFile(index)}
                        disabled={loading}
                      >
                        <Delete />
                      </IconButton>
                    }
                    sx={{
                      bgcolor: fileWithStatus.status === 'invalid' ? 'error.light' : 'transparent',
                    }}
                  >
                    <ListItemIcon>
                      {fileWithStatus.status === 'valid' ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Warning color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" noWrap>
                            {fileWithStatus.file.name}
                          </Typography>
                          <Chip
                            label={formatFileSize(fileWithStatus.file.size)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={fileWithStatus.error || `${fileWithStatus.file.type || 'Unknown type'}`}
                      secondaryTypographyProps={{
                        color: fileWithStatus.status === 'invalid' ? 'error' : 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          )}
          
          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={!canSubmit}
              onClick={handleOpenConfirmDialog}
              endIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
            >
              {loading
                ? t('upload.create.uploading', 'Uploading...')
                : `${t('upload.create.submit_prefix', 'Upload')} ${validFiles.length} ${validFiles.length === 1 ? t('upload.create.file', 'file') : t('upload.create.files', 'files')}`
              }
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCloseConfirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('upload.confirm.title', 'Confirm Upload')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              {t('upload.confirm.message', 'You are about to upload:')}
            </Typography>
            <Typography variant="h6" color="primary.main">
              {validFiles.length} {t('upload.confirm.files', 'file(s)')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('upload.confirm.client', 'Client:')} <strong>{clientName}</strong>
            </Typography>
          </Alert>
          
          <Typography variant="body2" color="text.secondary">
            {t('upload.confirm.info', 'Files will be processed automatically after upload. You can track progress on the batch details page.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={loading}>
            {t('upload.confirm.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {loading
              ? t('upload.confirm.uploading', 'Uploading...')
              : t('upload.confirm.confirm', 'Confirm & Upload')
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchCreation;
